import * as corgi from 'external/dev_april_corgi+/js/corgi';
import {
  Controller,
  Response,
} from 'external/dev_april_corgi+/js/corgi/controller';
import {CorgiEvent, DOM_MOUSE} from 'external/dev_april_corgi+/js/corgi/events';
import {ViewsService} from 'external/dev_april_corgi+/js/corgi/history/views_service';
import {ACTION} from 'external/dev_april_corgi+/js/emu/events';

import {GRAPH_NOT_YET_SAVED_ID} from './conventions';
import {Routes} from './route_controller';
import {Header, setTitle} from './header';
import {Button} from './widgets';
import {deleteGraph, listGraphs} from './workspace_storage';

interface GraphInfo {
  id: string;
  name: string;
  lastModified: number;
}

interface State {
  graphs: GraphInfo[];
}

type Deps = typeof GraphsListController.deps;

class GraphsListController extends Controller<{}, Deps, HTMLElement, State> {
  static deps() {
    return {
      services: {
        views: ViewsService<Routes>,
      },
    };
  }

  private readonly views: ViewsService<Routes>;

  constructor(response: Response<GraphsListController>) {
    super(response);
    this.views = response.deps.services.views;
    this.updateState({graphs: listGraphs()});
  }

  newGraph(): void {
    this.views.goTo('/graphs/' + GRAPH_NOT_YET_SAVED_ID);
  }

  openGraph(e: CorgiEvent<typeof DOM_MOUSE>): void {
    const id = e.actionElement.data('id')?.string();
    if (!id) return;
    this.views.goTo('/graphs/' + id);
  }

  deleteGraphClicked(e: CorgiEvent<typeof DOM_MOUSE>): void {
    // Prevent the row click from triggering
    e.detail.stopPropagation();
    const id = e.actionElement.data('id')?.string();
    const name = e.actionElement.data('name')?.string();
    if (!id) return;

    if (!confirm(`Delete "${name}"? This cannot be undone.`)) {
      return;
    }

    deleteGraph(id);
    this.updateState({
      ...this.state,
      graphs: listGraphs(),
    });
  }
}

export function GraphsListPage(
  args: {},
  state: State | undefined,
  updateState: (newState: State) => void,
) {
  if (!state) {
    state = {graphs: []}; // we do SSR when we don't know the graphs, delay loading for render
  }

  setTitle(undefined);

  return (
    <div
      className="content-center flex flex-col items-center justify-center min-h-full w-full"
      js={corgi.bind({
        controller: GraphsListController,
        state: [state, updateState],
        events: {render: 'wakeup'},
      })}
    >
      <div className="bg-neutral-800 drop-shadow p-8 oy-4 rounded w-2/3">
        <Header title="Graphs" />
        <div className="flex gap-2 items-center justify-end">
          <Button unboundEvents={{corgi: [[ACTION, 'newGraph']]}}>
            New Graph
          </Button>
        </div>

        {state.graphs.length === 0 ? (
          <div className="bg-neutral-800 p-8 rounded text-center text-neutral-400">
            No saved graphs yet. Click "New Graph" to create one.
          </div>
        ) : (
          <div className="bg-neutral-800 flex flex-col rounded">
            {state.graphs.map((graph, i) => (
              <GraphRow graph={graph} isLast={i === state!.graphs.length - 1} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function GraphRow({graph, isLast}: {graph: GraphInfo; isLast: boolean}) {
  const date = new Date(graph.lastModified);
  const dateStr = date.toLocaleDateString() + ' ' + date.toLocaleTimeString();

  return (
    <div
      className={
        'cursor-pointer flex hover:bg-neutral-700 items-center justify-between p-4 ' +
        (isLast ? '' : 'border-b border-neutral-700')
      }
      data={{id: graph.id}}
      unboundEvents={{click: 'openGraph'}}
    >
      <div className="flex flex-col gap-1">
        <div className="font-medium">{graph.name}</div>
        <div className="text-neutral-400 text-sm">{dateStr}</div>
      </div>
      <button
        className="bg-red-600 hover:bg-red-500 px-3 py-1 rounded text-sm text-white"
        data={{id: graph.id, name: graph.name}}
        unboundEvents={{click: 'deleteGraphClicked'}}
      >
        Delete
      </button>
    </div>
  );
}
